import type { Data } from '@repo/strapi-types'
import type { Metadata } from 'next'
import type { ReactNode } from 'react'
import { isValidElement } from 'react'
import { APP_DEFAULT_LOCALE, APP_LOCALES } from '@/constants/i18n.constants'
import { env } from '@/env.mjs'
import { routing } from '@/i18n/routing'
import { defaultMetaRobots, defaultRobots, metaRobots } from '@/lib/constants'

export const isDevelopment = () => env.NEXT_PUBLIC_APP_ENV === 'development'
export const isStaging = () => env.NEXT_PUBLIC_APP_ENV === 'staging'
export const isProduction = () => env.NEXT_PUBLIC_APP_ENV === 'production'

export const disablePagePregeneration = () => isDevelopment() || env.DISABLE_PAGE_PREGENERATION

/**
 * Recursively extracts text content from React children.
 *
 * @param children - React children (string, number, element, or array)
 * @returns Extracted text content as a string
 * @example
 * extractTextFromChildren(<Paragraph>Foo</Paragraph>) // "Foo"
 * extractTextFromChildren([<Icon />, "Click here"]) // "Click here"
 */
export function extractTextFromChildren(children: ReactNode): string {
	if (typeof children === 'string') return children
	if (typeof children === 'number') return String(children)
	if (!children) return ''

	if (Array.isArray(children)) {
		return children
			.map((child) => extractTextFromChildren(child))
			.join(' ')
			.trim()
	}

	if (isValidElement<{ children?: ReactNode }>(children)) {
		return extractTextFromChildren(children.props.children)
	}

	return ''
}

/**
 * Returning an empty array from generateStaticParams in {output: 'export'} mode yields a build error. Therefore
 * we need to return atleast a dummy value, even if it will result in a 404.
 *
 * @param params object containing parameter names and their fallback values
 * @returns object containing default locale and fallback values for each parameter
 * @example createFallbackPath({ slug: 'fallback' }) => {locale: "en", slug: "fallback"}
 * @example createFallbackPath({ slug: 'fallback', page: 'fallback' }) => {locale: "en", slug: "fallback", page: "fallback"}
 * @example createFallbackPath({ rest: ['fallback'] }) => {locale: "en", rest: ["fallback"]}
 */
export const createFallbackPath = (params: Record<string, string | string[]>) => ({
	locale: routing.defaultLocale,
	...params,
})

export const clearEmptyPartsFromHtml = <T extends string | undefined>(html?: T): T => {
	return (
		html
			// Removes all <br> and <br /> tags
			?.replace(/<br\s*\/?>/gim, '')
			// Removes all empty or whitespace-only paragraphs
			?.replace(/<p>(\s|&nbsp;)*<\/p>/gim, '') as T
	)
}

export const formatDefaultPageLabel = (label: string, allUppercase = true) => {
	if (!label) return ''

	const words = label.split('.')[1]?.split('-') || []

	return words
		.map((word, index) => {
			const capitalized = word.charAt(0).toUpperCase() + word.slice(1)
			if (!allUppercase && index === 0) {
				return word.charAt(0).toLowerCase() + word.slice(1)
			}
			return capitalized
		})
		.join('')
}

export const getOriginUrl = (additionalPath?: string) =>
	`${process.env.APP_PUBLIC_URL ?? window.location.origin}${additionalPath ?? ''}`

export const isPaginationInvalid = (page: string) => {
	const pageNumber = Number(page)
	return pageNumber === 1 || pageNumber === 0 || pageNumber < 0
}

export const resolveLocale = (locale: string) =>
	APP_LOCALES.includes(locale) ? locale : APP_DEFAULT_LOCALE

export const getSeoImages = (
	seo?: Data.Component<'shared.seo'> | null,
	options?: {
		image?: Data.Component<'utilities.basic-image'> | null
		// biome-ignore lint/suspicious/noExplicitAny: This is dynamic zone from Strapi page builder, so we cannot type it properly
		content?: any[] | null
	},
) => {
	const seoImageTwitter = getSeoImageMedia(seo, options, seo?.twitter?.images?.[0])
	const seoImageOg = getSeoImageMedia(seo, options, seo?.og?.image)

	return {
		og: seoImageOg?.url
			? [
					{
						url: seoImageOg.url,
						width: seoImageOg.width,
						height: seoImageOg.height,
						alt: seoImageOg.alternativeText,
					},
				]
			: undefined,
		twitter: seo?.twitter?.images?.length
			? seo?.twitter?.images?.map((image: Data.ContentType<'plugin::upload.file'>) => image.url)
			: [seoImageTwitter?.url],
	}
}

export const getSeoImageMedia = (
	seo?: Data.Component<'shared.seo'> | null,
	options?: {
		image?: Data.Component<'utilities.basic-image'> | null
		// biome-ignore lint/suspicious/noExplicitAny: This is dynamic zone from Strapi page builder, so we cannot type it properly
		content?: any[] | null
	},
	typeImage?: Data.Component<'utilities.basic-image'> | null,
) => {
	const centeredHero = options?.content?.find(
		(comp) => comp.__component === 'global.centered-banner',
	)
	const basicHero = options?.content?.find((comp) => comp.__component === 'global.hero')

	return (
		typeImage ??
		seo?.metaImage ??
		options?.image?.media ??
		basicHero?.primaryImage?.media ??
		centeredHero?.image?.media
	)
}
export const getMetaRobots = (
	metaRobotsString?: Data.Component<'shared.seo'>['metaRobots'] | null,
	customMetadata?: Metadata['robots'],
) => {
	const customMetadataFormatted =
		typeof customMetadata === 'string'
			? metaRobots[customMetadata.replace(/\s+/g, '')]
			: customMetadata

	return {
		...defaultMetaRobots,
		...metaRobots[metaRobotsString ?? defaultRobots],
		...customMetadataFormatted,
	}
}
